import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Plus, Activity, Loader2, Search, Pencil, Check, X, Filter, Landmark, CreditCard, HandCoins, HelpCircle, Eye, PiggyBank, ArrowUpDown, ArrowUp, ArrowDown, Calendar as CalendarIcon } from 'lucide-react';
import { NavigationProps } from "@/types/navigation";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from "../components/ui/breadcrumb";
import { SidebarTrigger } from "../components/ui/sidebar";
import { Separator } from "../components/ui/separator";
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/contexts/AuthContext';
import { db } from '@/firebase';
import { collection, query, where, getDocs, doc, updateDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { Transaction } from '@/types/transaction';
import * as React from "react"
import { useIsMobile } from "@/hooks/use-mobile"
import { Input } from "@/components/ui/input";
import { Combobox, ComboboxOption } from "@/components/ui/combobox";
import { normalizeCategory, shortenMerchantName } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react';
import { cn } from "@/lib/utils";
import { usePlaid } from "@/contexts/PlaidContext";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { TransactionDetailDialog } from '@/components/TransactionDetailDialog';
import { BankAccount } from '@/types/plaid'; // Adjust import if needed
import { URL_TO_PAGE_MAP } from "@/routes/routes"; // Import the URL mapping
import { mockTransactions, mockBankAccounts } from '@/lib/mockData'; // Import mock data
import { DateRange } from "react-day-picker";
import { Calendar } from "@/components/ui/calendar";
import { format, subDays } from "date-fns";
import { Label } from "@/components/ui/label";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { ResponsiveContainer, BarChart, CartesianGrid, XAxis, YAxis, Tooltip as RechartsTooltip, Bar } from 'recharts'; // Alias Recharts Tooltip
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"; // Import ToggleGroup
import { SpendingChart } from '@/components/charts/SpendingChart'; // Import the new chart

interface TransactionsPageProps extends NavigationProps {}

const TRANSACTIONS_PAGE_SIZE = 20;
const CHART_DATA_DAYS = 90; // Default MAX days for chart

// --- Define Categories for Combobox (using raw values and normalized labels) ---
const rawCategories = [
  "FOOD_AND_DRINK",
  "TRAVEL",
  "SHOPPING",
  "SERVICES",
  "TRANSFER_OUT", // Example raw category
  "TRANSFER_IN",  // Example raw category
  "INCOME",
  "RENT_AND_UTILITIES", // Example combined category
  "GENERAL_MERCHANDISE",
  "HEALTHCARE", // Added
  "ENTERTAINMENT", // Added
  "OTHER",
  "UNCATEGORIZED",
];

// const categoryOptions: ComboboxOption[] = rawCategories.map(cat => ({
//   value: cat, // Store the raw value
//   label: normalizeCategory(cat) // Display the normalized label
// }));
// --- End Define Categories ---

// Define a type for the raw category keys
type RawCategoryKey = typeof rawCategories[number];

// --- Define Budget Colors ---
const budgetColors: Record<string, string> = {
  Needs: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  Wants: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
  Travel: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
  Savings: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  Other: "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300",
};
// --- End Budget Colors ---

const categoryEmojis: Record<RawCategoryKey | 'UNCATEGORIZED', string> = {
  FOOD_AND_DRINK: "🍔", // Changed emoji slightly for variety
  TRAVEL: "✈️",
  SHOPPING: "🛒",
  SERVICES: "💼",
  TRANSFER_OUT: "💸",
  TRANSFER_IN: "💵",
  INCOME: "💰",
  RENT_AND_UTILITIES: "🏠",
  GENERAL_MERCHANDISE: "🎁",
  HEALTHCARE: "🩺", // Added
  ENTERTAINMENT: "🎬", // Added
  OTHER: "🤷",
  UNCATEGORIZED: "🤷",
  // Add more as needed
};

// --- Budget Mapping (Category -> Budget Info) ---
const categoryToBudgetInfo: Record<string, { title: string | null; url: string | null; color: string }> = {
  "TRAVEL": { title: "Travel", url: "/budgets/travel", color: budgetColors.Travel },
  "FOOD_AND_DRINK": { title: "Needs", url: "/budgets/overview", color: budgetColors.Needs }, // Map Needs categories to overview
  "RENT_AND_UTILITIES": { title: "Needs", url: "/budgets/overview", color: budgetColors.Needs },
  "SERVICES": { title: "Needs", url: "/budgets/overview", color: budgetColors.Needs }, // Assuming most services are needs
  "SHOPPING": { title: "Wants", url: "/budgets/wants", color: budgetColors.Wants },
  "GENERAL_MERCHANDISE": { title: "Wants", url: "/budgets/wants", color: budgetColors.Wants },
  "HEALTHCARE": { title: "Needs", url: "/budgets/overview", color: budgetColors.Needs }, // Added Healthcare as Needs
  "ENTERTAINMENT": { title: "Wants", url: "/budgets/wants", color: budgetColors.Wants }, // Added Entertainment as Wants
  "INCOME": { title: "Savings", url: "/budgets/savings", color: budgetColors.Savings },
  "TRANSFER_IN": { title: "Savings", url: "/budgets/savings", color: budgetColors.Savings },
  "TRANSFER_OUT": { title: "Other", url: "/budgets", color: budgetColors.Other }, // Map Other/Transfer Out to main budgets page
  "OTHER": { title: "Other", url: "/budgets", color: budgetColors.Other },
  "UNCATEGORIZED": { title: "Other", url: "/budgets", color: budgetColors.Other },
};

const defaultBudgetInfo = { title: "Other", url: "/budgets", color: budgetColors.Other }; // Default to main budgets page with Other color

const getBudgetInfoForCategory = (category: string): { title: string | null; url: string | null; color: string } => {
  return categoryToBudgetInfo[category] || defaultBudgetInfo; // Default info if no mapping found
};
// --- End Budget Mapping ---

// --- Account Icon Mapping ---
const accountTypeIcons: Record<string, React.ElementType> = {
  depository: Landmark,
  credit: CreditCard,
  loan: HandCoins,
  investment: HelpCircle,
  other: HelpCircle,
};
// --- End Account Icon Mapping ---

// --- Reintroduce Sort Types ---
type SortKey = 'date' | 'merchant_name' | 'category' | 'amount';
type SortDirection = 'asc' | 'desc';

interface SortConfig {
  key: SortKey;
  direction: SortDirection;
}
// --- End Sort Types ---

export default function TransactionsPage({ onNavigate }: TransactionsPageProps) {
  const { currentUser } = useAuth();
  const isMobile = useIsMobile();
  const { bankAccounts } = usePlaid();
  const [transactions, setTransactions] = useState<Transaction[]>(mockTransactions);
  const [viewingTransaction, setViewingTransaction] = useState<Transaction | null>(null);

  const [editingTransactionId, setEditingTransactionId] = useState<string | null>(null);
  const [currentEditCategory, setCurrentEditCategory] = useState<string>('');
  const [currentEditEmoji, setCurrentEditEmoji] = useState<string>('');
  const [isFilterPopoverOpen, setIsFilterPopoverOpen] = useState(false);
  const [filterDateRange, setFilterDateRange] = useState<DateRange | undefined>(undefined);
  const [filterCategory, setFilterCategory] = useState<string | undefined>(undefined);
  const [filterMinAmount, setFilterMinAmount] = useState<string>('');
  const [filterMaxAmount, setFilterMaxAmount] = useState<string>('');

  // --- Search State ---
  const [searchQuery, setSearchQuery] = useState('');
  // --- End Search State ---

  const [userCategorySettings, setUserCategorySettings] = useState(new Map());
  const [isCategorySettingsLoading, setIsCategorySettingsLoading] = useState(true);

  // --- Reintroduce Sort State ---
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'date', direction: 'desc' });
  // --- End Sort State ---

  // --- Pagination State ---
  const [currentPage, setCurrentPage] = useState(1);
  // --- End Pagination State ---

  // --- Chart State (REMOVED) ---
  // const [chartTimeRange, setChartTimeRange] = useState<number>(7); // Default to 7 days
  // --- End Chart State ---

  // --- Chart Data Processing (MOVED to SpendingChart component) ---
  // const chartData = useMemo(() => {
  // ... calculation logic ...
  // }, [transactions, chartTimeRange]);
  // --- End Chart Data Processing ---

  const getEmojiForCategory = useCallback((rawCategoryKey: string): string => {
    // 1. Check user settings first
    const userSetting = userCategorySettings.get(rawCategoryKey);
    if (userSetting?.emoji) {
        return userSetting.emoji;
    }
    // 2. Fallback to hardcoded map
    return categoryEmojis[rawCategoryKey as RawCategoryKey | 'UNCATEGORIZED'] || categoryEmojis['UNCATEGORIZED'];
  }, [userCategorySettings]);

  const dynamicCategoryOptions = useMemo(() => {
    const allCategoryKeys = new Set<string>([...rawCategories]);
    userCategorySettings.forEach((_setting, key) => allCategoryKeys.add(key));

    const options = Array.from(allCategoryKeys).map(key => ({
      value: key,
      label: normalizeCategory(key)
    }));

    options.sort((a, b) => a.label.localeCompare(b.label));

    return options;
  }, [userCategorySettings]);

  useEffect(() => {
    if (currentUser) {
        setTransactions(mockTransactions);
        fetchUserCategorySettings();
    } else {
      setTransactions([]);
      setUserCategorySettings(new Map());
      setIsCategorySettingsLoading(true);
    }
  }, [currentUser]);

  // --- Reintroduce Sort Functions ---
  const handleSort = (key: SortKey) => {
    let direction: SortDirection = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    console.log("Sorting by:", key, direction);
  };

  const renderSortIcon = (key: SortKey) => {
    if (sortConfig.key !== key) {
      return <ArrowUpDown className="ml-2 h-4 w-4 opacity-30" />;
    }
    return sortConfig.direction === 'asc' ?
      <ArrowUp className="ml-2 h-4 w-4" />
      : <ArrowDown className="ml-2 h-4 w-4" />;
  };
  // --- End Sort Functions ---

  // --- Client-Side Sorting Logic ---
  const sortedTransactions = useMemo(() => {
    let sortableItems = [...transactions]; // Create a mutable copy
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        let aValue: string | number | null | undefined;
        let bValue: string | number | null | undefined;

        // Select values based on sort key
        switch (sortConfig.key) {
          case 'date':
            aValue = a.date;
            bValue = b.date;
            break;
          case 'merchant_name':
            // Use merchant_name, fallback to name, then 'N/A'
            aValue = a.merchant_name || a.name || 'N/A';
            bValue = b.merchant_name || b.name || 'N/A';
            break;
          case 'category':
            aValue = a.personal_finance_category?.primary || a.category?.[0] || 'UNCATEGORIZED';
            bValue = b.personal_finance_category?.primary || b.category?.[0] || 'UNCATEGORIZED';
            break;
          case 'amount':
            aValue = a.amount;
            bValue = b.amount;
            break;
          default:
            return 0; // Should not happen
        }

        // Basic comparison for strings and numbers
        if (aValue === null || aValue === undefined) aValue = ''; // Handle null/undefined for sorting
        if (bValue === null || bValue === undefined) bValue = '';

        // Comparison logic
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [transactions, sortConfig]); // Re-sort when transactions or sortConfig change
  // --- End Client-Side Sorting Logic ---

  // --- Filtering Logic ---
  const filteredTransactions = useMemo(() => {
    let items = [...sortedTransactions]; // Start with sorted items

    // Apply Search Filter (Case-insensitive)
    if (searchQuery.trim()) {
      const lowerCaseQuery = searchQuery.toLowerCase();
      items = items.filter(tx =>
        (tx.name?.toLowerCase().includes(lowerCaseQuery)) ||
        (tx.merchant_name?.toLowerCase().includes(lowerCaseQuery)) ||
        (normalizeCategory(tx.personal_finance_category?.primary || tx.category?.[0] || 'UNCATEGORIZED').toLowerCase().includes(lowerCaseQuery))
      );
    }

    // Apply Date Filter
    if (filterDateRange?.from) {
      items = items.filter(tx => {
        try {
          const txDate = new Date(tx.date + 'T00:00:00Z'); // Treat date as UTC
          const fromDate = new Date(filterDateRange.from!.setUTCHours(0, 0, 0, 0));
          if (!filterDateRange.to) {
            return txDate.getTime() === fromDate.getTime(); // Single day selection
          } else {
            const toDate = new Date(filterDateRange.to!.setUTCHours(23, 59, 59, 999));
            return txDate >= fromDate && txDate <= toDate;
          }
        } catch (e) {
          console.error("Error comparing dates:", tx.date, e);
          return false;
        }
      });
    }

    // Apply Category Filter
    if (filterCategory) {
      items = items.filter(tx => (tx.personal_finance_category?.primary || tx.category?.[0] || 'UNCATEGORIZED') === filterCategory);
    }

    // Apply Amount Filter
    const min = parseFloat(filterMinAmount);
    const max = parseFloat(filterMaxAmount);
    if (!isNaN(min)) {
      items = items.filter(tx => tx.amount >= min);
    }
    if (!isNaN(max)) {
      items = items.filter(tx => tx.amount <= max);
    }

    return items;
  }, [sortedTransactions, filterDateRange, filterCategory, filterMinAmount, filterMaxAmount, searchQuery]);
  // --- End Filtering Logic ---

  // Effect to reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterDateRange, filterCategory, filterMinAmount, filterMaxAmount, searchQuery]);

  // --- Pagination Logic ---
  const totalPages = Math.ceil(filteredTransactions.length / TRANSACTIONS_PAGE_SIZE);

  const paginatedTransactions = useMemo(() => {
    const startIndex = (currentPage - 1) * TRANSACTIONS_PAGE_SIZE;
    const endIndex = startIndex + TRANSACTIONS_PAGE_SIZE;
    return filteredTransactions.slice(startIndex, endIndex);
  }, [filteredTransactions, currentPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePreviousPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };
  // --- End Pagination Logic ---

  const handleStartEditing = (transaction: Transaction) => {
    setEditingTransactionId(transaction.id);
    const rawCategory = transaction.personal_finance_category?.primary || transaction.category?.[0] || 'UNCATEGORIZED';
    const currentEmoji = getEmojiForCategory(rawCategory);
    setCurrentEditCategory(rawCategory);
    setCurrentEditEmoji(currentEmoji);
  };

  const handleCancelEditing = () => {
    setEditingTransactionId(null);
    setCurrentEditCategory('');
    setCurrentEditEmoji('');
  };

  const handleSaveCategory = async () => {
    if (!editingTransactionId || !currentEditCategory || !currentUser || !db) {
       console.warn("Cannot save category: missing transaction ID, category, user, or database connection.");
       return;
    }

    const categoryIdToSave = currentEditCategory;
    const categoryEmojiToSave = currentEditEmoji || '🤷';

    console.log(`Saving transaction ${editingTransactionId}. Category: ${categoryIdToSave}, Emoji: ${categoryEmojiToSave}`);

    const transactionRef = doc(db, "transactions", editingTransactionId);
    const categorySettingsRef = doc(db, "userCategories", categoryIdToSave);

    let originalCategory: string | undefined;

    const existingTx = transactions.find(tx => tx.id === editingTransactionId);
    if (!existingTx) {
        console.error("Transaction to edit not found locally!");
        return;
    }
    originalCategory = existingTx.personal_finance_category?.primary || existingTx.category?.[0] || 'UNCATEGORIZED';

    const currentStoredEmoji = getEmojiForCategory(categoryIdToSave);
    const emojiHasChanged = currentStoredEmoji !== categoryEmojiToSave;

    const isNewCategory = !rawCategories.includes(categoryIdToSave) && !userCategorySettings.has(categoryIdToSave);

    console.log(`Is new category? ${isNewCategory}. Emoji changed? ${emojiHasChanged}`);

    setTransactions(prev =>
        prev.map(tx =>
            tx.id === editingTransactionId
                ? {
                    ...tx,
                    personal_finance_category: {
                        ...tx.personal_finance_category,
                        primary: categoryIdToSave,
                        detailed: categoryIdToSave,
                    },
                    category: [categoryIdToSave]
                  }
                : tx
        )
    );

    if (isNewCategory || emojiHasChanged) {
        const normalizedName = normalizeCategory(categoryIdToSave);
        setUserCategorySettings(prevMap => {
            const newMap = new Map(prevMap);
            newMap.set(categoryIdToSave, { name: normalizedName, emoji: categoryEmojiToSave });
            return newMap;
        });
    }

    try {
        await updateDoc(transactionRef, {
            personal_finance_category: { 
              ...(existingTx?.personal_finance_category || {}),
              primary: categoryIdToSave,
              detailed: categoryIdToSave, 
            },
            category: [categoryIdToSave], 
            updatedAt: serverTimestamp(), 
        });
        console.log("Transaction category assignment updated successfully in Firestore.");

        if (isNewCategory || emojiHasChanged) {
            const normalizedName = normalizeCategory(categoryIdToSave);
            await setDoc(categorySettingsRef, { 
                userId: currentUser.uid, 
                name: normalizedName,
                emoji: categoryEmojiToSave 
            }, { merge: true });
            console.log(`User category setting for ${categoryIdToSave} ${isNewCategory ? 'created' : 'updated'} successfully in Firestore.`);
        }
    } catch (error) {
        console.error("Error updating Firestore:", error);
        setTransactions(prev =>
            prev.map(tx =>
                tx.id === editingTransactionId
                    ? { ...existingTx }
                    : tx
            )
        );
        if (isNewCategory || emojiHasChanged) {
           console.warn("Firestore update failed. Local category settings might be out of sync. Consider refetching.");
           setUserCategorySettings(prevMap => {
                const newMap = new Map(prevMap);
                if (isNewCategory) {
                    newMap.delete(categoryIdToSave);
                } else if (emojiHasChanged) {
                    const previousEmoji = getEmojiForCategory(categoryIdToSave);
                    const existingSetting = prevMap.get(categoryIdToSave);
                    if (existingSetting) {
                         newMap.set(categoryIdToSave, { ...existingSetting, emoji: previousEmoji });
                    } else {
                         newMap.delete(categoryIdToSave);
                    }
                }
                return newMap;
           });
        }
    }

    setEditingTransactionId(null);
    setCurrentEditCategory('');
    setCurrentEditEmoji('');
  };

  const getAccountInfo = useCallback((plaidAccountId: string) => {
    return mockBankAccounts.find(acc => acc.plaidAccountId === plaidAccountId);
  }, []);

  const getAccountIcon = useCallback((accountType: string | undefined): React.ElementType => {
    return accountType ? accountTypeIcons[accountType] || HelpCircle : HelpCircle;
  }, []);

  const fetchUserCategorySettings = useCallback(async () => {
    if (!currentUser || !db) {
      console.log("User or DB not available, skipping category settings fetch.");
      setUserCategorySettings(new Map());
      setIsCategorySettingsLoading(false);
      return;
    }
    console.log("Fetching user category settings...");
    setIsCategorySettingsLoading(true);
    const settingsMap = new Map<string, { name: string; emoji: string }>();
    try {
      const categoriesCol = collection(db, 'userCategories');
      const q = query(categoriesCol, where("userId", "==", currentUser.uid));
      const querySnapshot = await getDocs(q);
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        settingsMap.set(doc.id, { 
          name: data.name || normalizeCategory(doc.id),
          emoji: data.emoji || '🤷' 
        });
      });
      console.log(`Fetched ${settingsMap.size} user category settings.`);
      setUserCategorySettings(settingsMap);
    } catch (error) {
      console.error("Error fetching user category settings:", error);
      setUserCategorySettings(new Map());
    } finally {
      setIsCategorySettingsLoading(false);
    }
  }, [currentUser]);

  return (
    <>
      <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
        <div className="flex items-center gap-2 px-4">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbPage>Transactions</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>   
          </Breadcrumb>
        </div>
      </header>

      <main className="flex-1 overflow-auto p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Transactions</h1>
            <p className="text-muted-foreground">
              View and manage your financial transactions
            </p>
          </div>
        </div>
        
        <div className="grid gap-6">
          {/* Spending Chart Card */}
          <SpendingChart transactions={transactions} days={30} /> {/* Use the new chart component */}

          {/* Transactions Table Card */}
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <CardTitle>Recent Transactions</CardTitle>
                  <CardDescription>
                    List of your recent financial activities.
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2 mt-4 sm:mt-0 sm:ml-4">
                  {/* Search Input */}
                  <div className="relative w-full sm:w-auto">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="search"
                      placeholder="Search transactions..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-8 sm:w-[200px] md:w-[250px] lg:w-[300px] h-8"
                    />
                  </div>
                  {/* Filter Popover */}
                  <Popover open={isFilterPopoverOpen} onOpenChange={setIsFilterPopoverOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="icon" className="h-8 w-8 shrink-0">
                        <Filter className="h-4 w-4" />
                        <span className="sr-only">Filter</span>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80">
                      <div className="grid gap-4">
                        <div className="space-y-2">
                          <h4 className="font-medium leading-none">Filters</h4>
                          <p className="text-sm text-muted-foreground">
                            Filter transactions by date, category, or amount.
                          </p>
                        </div>
                        <div className="grid gap-4">
                          <div className="grid grid-cols-1 gap-2">
                            <Label htmlFor="date-range">Date Range</Label>
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button
                                  id="date-range"
                                  variant={"outline"}
                                  className={cn(
                                    "w-full justify-start text-left font-normal h-8",
                                    !filterDateRange && "text-muted-foreground"
                                  )}
                                >
                                  <CalendarIcon className="mr-2 h-4 w-4" />
                                  {filterDateRange?.from ? (
                                    filterDateRange.to ? (
                                      <>
                                        {format(filterDateRange.from, "LLL dd, y")} -{" "}
                                        {format(filterDateRange.to, "LLL dd, y")}
                                      </>
                                    ) : (
                                      format(filterDateRange.from, "LLL dd, y")
                                    )
                                  ) : (
                                    <span>Pick a date range</span>
                                  )}
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0" align="start">
                                <Calendar
                                  initialFocus
                                  mode="range"
                                  defaultMonth={filterDateRange?.from}
                                  selected={filterDateRange}
                                  onSelect={setFilterDateRange}
                                  numberOfMonths={2}
                                />
                              </PopoverContent>
                            </Popover>
                          </div>

                          <div className="grid grid-cols-1 gap-2">
                            <Label htmlFor="category-filter">Category</Label>
                             <Combobox
                               options={dynamicCategoryOptions}
                               value={filterCategory}
                               onChange={(newValue) => setFilterCategory(newValue)}
                               placeholder="Select category..."
                               searchPlaceholder="Search categories..."
                               emptyPlaceholder="No categories found."
                               className="h-8 text-xs"
                              />
                          </div>

                          <div className="grid grid-cols-2 gap-2">
                            <div className="grid grid-cols-1 gap-2">
                              <Label htmlFor="min-amount">Min Amount</Label>
                              <Input
                                id="min-amount"
                                type="number"
                                placeholder="0.00"
                                value={filterMinAmount}
                                onChange={(e) => setFilterMinAmount(e.target.value)}
                                className="h-8"
                              />
                            </div>
                            <div className="grid grid-cols-1 gap-2">
                              <Label htmlFor="max-amount">Max Amount</Label>
                              <Input
                                id="max-amount"
                                type="number"
                                placeholder="1000.00"
                                value={filterMaxAmount}
                                onChange={(e) => setFilterMaxAmount(e.target.value)}
                                className="h-8"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button variant="outline" size="sm" onClick={() => {
                                setFilterDateRange(undefined);
                                setFilterCategory(undefined);
                                setFilterMinAmount('');
                                setFilterMaxAmount('');
                                setIsFilterPopoverOpen(false);
                            }}>Clear</Button>
                            <Button size="sm" onClick={() => {
                                setIsFilterPopoverOpen(false);
                            }}>Apply</Button>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </CardHeader>
            <CardContent className="px-2 pt-4 sm:px-6 sm:pt-6">
              {transactions.length === 0 ? (
                <div className="flex h-40 items-center justify-center border rounded-md border-dashed p-4">
                  <div className="flex flex-col items-center gap-2 text-center">
                    <Activity className="h-10 w-10 text-muted-foreground" />
                    <h3 className="text-lg font-medium">
                      {filterDateRange ? 'No Transactions Found' : 'No Transactions Yet'}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {filterDateRange
                        ? 'Try refining your search or check the merchant name.'
                        : 'Link an account or sync existing ones to see transactions.'}
                    </p>
                  </div>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/40 hover:bg-muted/40 border-b">
                      <TableHead>
                        <Button variant="ghost" onClick={() => handleSort('date')} className="px-0">
                          Date
                          {renderSortIcon('date')}
                        </Button>
                      </TableHead>
                      <TableHead className="w-10 p-2"></TableHead>
                      {!isMobile && (
                        <TableHead>
                          <Button variant="ghost" onClick={() => handleSort('merchant_name')} className="px-0">
                            Merchant
                            {renderSortIcon('merchant_name')}
                          </Button>
                        </TableHead>
                      )}
                      {!isMobile && <TableHead>Account</TableHead>}
                      {!isMobile && <TableHead>Budget</TableHead>}
                      <TableHead>
                        <Button variant="ghost" onClick={() => handleSort('category')} className="px-0">
                          Category
                          {renderSortIcon('category')}
                        </Button>
                      </TableHead>
                      <TableHead className="text-right">
                        <Button variant="ghost" onClick={() => handleSort('amount')} className="px-0">
                          Amount
                          {renderSortIcon('amount')}
                        </Button>
                      </TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedTransactions.map((tx, idx) => {
                      const isEditing = editingTransactionId === tx.id;
                      let formattedDate = tx.date;
                      let mobileDateLine1 = '';
                      let mobileDateLine2 = '';

                      try {
                        const date = new Date(tx.date + 'T00:00:00Z');
                        formattedDate = date.toLocaleDateString("en-US", {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                          timeZone: 'UTC'
                        });
                        mobileDateLine1 = date.toLocaleDateString("en-US", { month: 'short', day: 'numeric', timeZone: 'UTC' });
                        mobileDateLine2 = date.toLocaleDateString("en-US", { year: 'numeric', timeZone: 'UTC' });
                      } catch (e) {
                        console.error("Error parsing date string:", tx.date, e);
                      }
                      const rawCategory = tx.personal_finance_category?.primary || tx.category?.[0] || 'UNCATEGORIZED';

                      return (
                        <TableRow key={tx.id || tx.transaction_id} className={`${idx % 2 !== 0 ? 'bg-muted/50' : ''} hover:bg-muted/90`}>
                          <TableCell className="font-medium">
                            {isMobile ? (
                              <div className="flex flex-col leading-tight">
                                <span>{mobileDateLine1}</span>
                                <span className="text-xs text-muted-foreground">{mobileDateLine2}</span>
                              </div>
                            ) : (
                              formattedDate
                            )}
                          </TableCell>
                          <TableCell className="w-10 p-2">
                            {tx.logo_url ? (
                              <img
                                src={tx.logo_url}
                                alt={`${tx.merchant_name || tx.name || 'Transaction'} logo`}
                                className="h-6 w-6 rounded-sm object-contain"
                                onError={(e) => (e.currentTarget.style.display = 'none')}
                              />
                            ) : (
                              <div className="h-6 w-6 rounded-sm bg-muted flex items-center justify-center text-xs text-muted-foreground">
                                 {tx.merchant_name ? tx.merchant_name.charAt(0).toUpperCase() : '?'} 
                              </div>
                            )}
                          </TableCell>
                          {!isMobile && <TableCell>{shortenMerchantName(tx.merchant_name || tx.name || 'N/A')}</TableCell>}
                          {!isMobile && (
                            <TableCell>
                              {(() => {
                                const account = getAccountInfo(tx.account_id);
                                if (!account) {
                                  return <span className="text-sm text-muted-foreground">Unknown</span>;
                                }
                                const AccountIcon = getAccountIcon(account.type);
                                return (
                                  <TooltipProvider delayDuration={100}>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                         <span className="flex items-center justify-start cursor-default h-full">
                                          <AccountIcon className="h-4 w-4" />
                                          {account.mask && <span className="ml-1 text-xs text-muted-foreground">{`**** ${account.mask}`}</span>}
                                         </span>
                                      </TooltipTrigger>
                                      <TooltipContent side="top" align="start">
                                        <p className="font-semibold">{account.name}</p>
                                        <p className="text-xs text-muted-foreground">
                                          {account.mask ? `**** ${account.mask}` : 'Details unavailable'}
                                        </p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                );
                              })()}
                            </TableCell>
                          )}
                          {!isMobile && (
                            <TableCell>
                              {(() => {
                                const budgetInfo = getBudgetInfoForCategory(rawCategory);
                                const page = budgetInfo.url ? URL_TO_PAGE_MAP[budgetInfo.url] : undefined;

                                return (
                                  <div className="flex items-center gap-2">
                                    {budgetInfo.title && (
                                      <Button
                                        variant="link"
                                        className={cn(
                                          "h-auto p-0 m-0 text-xs text-muted-foreground hover:text-foreground",
                                          !budgetInfo.url || !page ? "cursor-default" : ""
                                        )}
                                        onClick={() => budgetInfo.url && page && onNavigate(page)}
                                        disabled={!budgetInfo.url || !page}
                                        title={`Go to ${budgetInfo.title} budget`}
                                      >
                                        <Badge variant="secondary" className={cn(
                                          "cursor-pointer hover:bg-muted",
                                          budgetInfo.color
                                        )}>
                                          {budgetInfo.title}
                                        </Badge>
                                      </Button>
                                    )}
                                  </div>
                                );
                              })()}
                            </TableCell>
                          )}
                          <TableCell>
                            {isEditing ? (
                              <div className="flex items-center gap-2">
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <Button variant="outline" size="icon" className="h-8 w-8 shrink-0">
                                      <span className="text-base" role="img" aria-label="current emoji">
                                        {currentEditEmoji || '❓'}
                                      </span>
                                      <span className="sr-only">Select Emoji</span>
                                    </Button>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-auto p-0">
                                    <EmojiPicker
                                      onEmojiClick={(emojiData: EmojiClickData) => {
                                        setCurrentEditEmoji(emojiData.emoji);
                                      }}
                                      height={350}
                                      width={300}
                                    />
                                  </PopoverContent>
                                </Popover>
                                <Combobox
                                  options={dynamicCategoryOptions}
                                  value={currentEditCategory}
                                  onChange={(newValue) => {
                                    setCurrentEditCategory(newValue);
                                    setCurrentEditEmoji(getEmojiForCategory(newValue));
                                  }}
                                  placeholder="Select or type category..."
                                  searchPlaceholder="Search categories..."
                                  emptyPlaceholder="Type to add new category..."
                                  className="h-8 w-[180px] text-xs"
                                />
                              </div>
                            ) : (
                              <div className="flex items-center">
                                <span className="mr-2 text-base" role="img" aria-label={rawCategory}>
                                  {getEmojiForCategory(rawCategory)}
                                </span>
                                <Badge variant="outline">
                                  {normalizeCategory(rawCategory)}
                                </Badge>
                              </div>
                            )}
                          </TableCell>
                          <TableCell className={cn(
                            "text-right",
                            tx.amount < 0 ? '' : 'text-green-600',
                            rawCategory === "INCOME" && "filter blur-sm"
                          )}>
                            {new Intl.NumberFormat('en-US', {
                              style: 'currency',
                              currency: tx.iso_currency_code || 'USD',
                            }).format(tx.amount)}
                          </TableCell>
                          <TableCell className="text-right">
                            {isEditing ? (
                              <>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleSaveCategory()}
                                  className="h-8 w-8 mr-1 text-green-600 hover:text-green-700"
                                >
                                  <Check className="h-4 w-4" />
                                  <span className="sr-only">Save Category</span>
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={handleCancelEditing}
                                  className="h-8 w-8 text-red-600 hover:text-red-700"
                                >
                                  <X className="h-4 w-4" />
                                  <span className="sr-only">Cancel Edit</span>
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => setViewingTransaction(tx)}
                                  className="h-8 w-8 mr-1"
                                >
                                  <Eye className="h-4 w-4" />
                                  <span className="sr-only">View Details</span>
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleStartEditing(tx)}
                                  className="h-8 w-8"
                                >
                                  <Pencil className="h-4 w-4" />
                                  <span className="sr-only">Edit Transaction</span>
                                </Button>
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
            {totalPages > 1 && (
              <CardFooter className="flex justify-center items-center py-4">
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        href="#"
                        onClick={(e) => { e.preventDefault(); handlePreviousPage(); }}
                        isActive={currentPage > 1}
                      />
                    </PaginationItem>
                    {[...Array(totalPages)].map((_, i) => {
                      const page = i + 1;
                      // Basic logic to show first, last, current, and nearby pages
                      const showPage = page === 1 || page === totalPages || page === currentPage || Math.abs(page - currentPage) <= 1;
                      const showEllipsisBefore = page === currentPage - 2 && currentPage > 3;
                      const showEllipsisAfter = page === currentPage + 2 && currentPage < totalPages - 2;

                      if (showEllipsisBefore) {
                        return <PaginationItem key={`ellipsis-start-${i}`}><PaginationEllipsis /></PaginationItem>;
                      }
                      if (showEllipsisAfter) {
                        return <PaginationItem key={`ellipsis-end-${i}`}><PaginationEllipsis /></PaginationItem>;
                      }
                      if (showPage) {
                        return (
                          <PaginationItem key={page}>
                            <PaginationLink
                              href="#"
                              onClick={(e) => { e.preventDefault(); handlePageChange(page); }}
                              isActive={currentPage === page}
                            >
                              {page}
                            </PaginationLink>
                          </PaginationItem>
                        );
                      }
                      return null;
                    })}
                    <PaginationItem>
                      <PaginationNext
                        href="#"
                        onClick={(e) => { e.preventDefault(); handleNextPage(); }}
                        isActive={currentPage < totalPages}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </CardFooter>
            )}
          </Card>
        </div>
      </main>

      <TransactionDetailDialog
        transaction={viewingTransaction}
        isOpen={!!viewingTransaction}
        onClose={() => setViewingTransaction(null)}
        userCategorySettings={userCategorySettings}
        bankAccounts={bankAccounts}
      />
    </>
  );
}
